/**
 * The selected room in the assembler's 3D view, the counterpart of the 2D
 * view's selection outline: the room's footprint traced in the selection blue
 * as a thin band at floor level and another along its wall tops, and its floor
 * washed a little toward the same blue. Built at compose time on top of the
 * cached room scenes, so selecting a room never rebuilds one.
 */
import { type FloorPlanRoom, type Point, resolveScene, roomPlacementTransform } from '@kaiizen/kai-floor-plan';
import { PALETTE, type RGB } from '../editor-model';
import type { Vec3 } from '../math3d';
import { ALWAYS, type MeshBuilder } from '../view3d';

/** Band width (ft): about the 2D outline's stroke at the assembler's usual zoom. */
const BAND_WIDTH = 0.5;
const HALF_BAND_WIDTH = BAND_WIDTH / 2;
/** How far a band stands above the floor or the wall tops it traces (ft). */
const BAND_HEIGHT = 0.25;
/** How much of the selection blue washes over the selected room's floor. */
const FLOOR_WASH = 0.25;
/** A floor top sits at the room's elevation; a vertex this close counts (ft). */
const FLOOR_TOP_TOLERANCE = 0.05;

const mixToward = (color: RGB, target: RGB, amount: number): RGB => [
  color[0] + (target[0] - color[0]) * amount,
  color[1] + (target[1] - color[1]) * amount,
  color[2] + (target[2] - color[2]) * amount,
];

/** Recolor for the selected room's vertices: its floor tops, at elevation `floor`, washed toward the selection blue; everything else as it is. */
export const washSelectedFloor = (floor: number) => (color: RGB, point: Vec3, normal: Vec3): RGB =>
  normal[1] > 0.99 && Math.abs(point[1] - floor) <= FLOOR_TOP_TOLERANCE ? mixToward(color, PALETTE.accent, FLOOR_WASH) : color;

/** One band: a thin box along the segment start to end, overlapping its neighbors at the corners, standing from `base` up. */
function appendBand(mesh: MeshBuilder, start: Point, end: Point, base: number): void {
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  if (length < 1e-9) return;
  const along = { x: (end.x - start.x) / length, y: (end.y - start.y) / length };
  const across = { x: -along.y * HALF_BAND_WIDTH, y: along.x * HALF_BAND_WIDTH };
  const from = { x: start.x - along.x * HALF_BAND_WIDTH, y: start.y - along.y * HALF_BAND_WIDTH };
  const to = { x: end.x + along.x * HALF_BAND_WIDTH, y: end.y + along.y * HALF_BAND_WIDTH };
  const top = base + BAND_HEIGHT;
  const corner = (point: Point, side: number, height: number): Vec3 => [point.x + side * across.x, height, point.y + side * across.y];
  const blue = PALETTE.accent;
  mesh.quad(corner(from, 1, top), corner(to, 1, top), corner(to, -1, top), corner(from, -1, top), [0, 1, 0], blue);
  mesh.quad(corner(from, 1, base), corner(to, 1, base), corner(to, 1, top), corner(from, 1, top), [across.x, 0, across.y], blue);
  mesh.quad(corner(from, -1, base), corner(to, -1, base), corner(to, -1, top), corner(from, -1, top), [-across.x, 0, -across.y], blue);
  mesh.quad(corner(from, 1, base), corner(from, -1, base), corner(from, -1, top), corner(from, 1, top), [-along.x, 0, -along.y], blue);
  mesh.quad(corner(to, 1, base), corner(to, -1, base), corner(to, -1, top), corner(to, 1, top), [along.x, 0, along.y], blue);
}

/**
 * Trace the room's placed footprint (every ring, alcoves and cut corners
 * included) as a band at floor level, and along its wall tops when it has a
 * height. `center` is the composed scene's center in plan; `floor` the room's
 * elevation. The bands are drawn only; they add no pick target.
 */
export function appendSelectedRoomOutline(mesh: MeshBuilder, room: FloorPlanRoom, center: Point, floor: number): void {
  const toPlan = roomPlacementTransform(room);
  const rings = resolveScene(room).roomSolid.flat().map(ring =>
    ring.map(point => {
      const placed = toPlan(point);
      return { x: placed.x - center.x, y: placed.y - center.y };
    })
  );
  const heights = room.height ? [floor, floor + room.height] : [floor];
  mesh.begin(ALWAYS);
  for (const ring of rings) {
    ring.forEach((start, index) => {
      for (const base of heights) appendBand(mesh, start, ring[(index + 1) % ring.length], base);
    });
  }
  mesh.end();
}
